import {
  request,
  useEndpointsContext,
  type IHedgeRule,
  type MinimalSubscriptionResponse,
  type ModeEnum,
} from '@talos/kyoko';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { v4 as uuid } from 'uuid';
import type {
  GlobalHedgeCommandPayload,
  HedgeCommandResponse,
  HedgeRulePatchPayload,
  HedgeRulePostPayload,
  SingleHedgeCommandPayload,
} from '../../../../components/AutoHedging/types';

const HEDGE_RULE_ID = 'HEDGE_RULE' as const;

const generateHedgeRuleQueryID = (id?: string) => {
  return [HEDGE_RULE_ID, '/', id];
};

export function useHedgeRuleQuery(id?: string) {
  const { orgApiEndpoint } = useEndpointsContext();
  const queryClient = useQueryClient();

  return useQuery<IHedgeRule | null>({
    enabled: !!id,
    queryKey: generateHedgeRuleQueryID(id),
    staleTime: 0,
    // Keep polling while a rule exists; stop when missing so we do not spin on deleted IDs.
    refetchInterval: query => (query.state.data ? 10_000 : false),
    placeholderData: () => {
      const allHedgeRulesQueryData = queryClient.getQueryData<ReturnType<typeof useHedgeRulesQuery>['data']>([
        HEDGE_RULE_ID,
      ]);
      if (allHedgeRulesQueryData) {
        return allHedgeRulesQueryData.data.find(hedgeRule => hedgeRule.HedgeRuleID === id) ?? null;
      }
      return undefined;
    },
    queryFn: async () => {
      const response = await request<MinimalSubscriptionResponse<IHedgeRule>>(
        'GET',
        `${orgApiEndpoint}/hedge-rules/${id}`
      );
      // TanStack Query v5 forbids `undefined` from queryFn; empty list means the rule is gone.
      return response.data[0] ?? null;
    },
  });
}

export function useHedgeRulesQuery() {
  const { orgApiEndpoint } = useEndpointsContext();

  return useQuery<MinimalSubscriptionResponse<IHedgeRule>>({
    queryKey: [HEDGE_RULE_ID],
    queryFn: async () => request<MinimalSubscriptionResponse<IHedgeRule>>('GET', `${orgApiEndpoint}/hedge-rules`),
  });
}

export interface HedgeRuleCreationMutationArgs extends HedgeRulePostPayload {
  Mode: ModeEnum;
}
export function useHedgeRuleCreationMutation() {
  const { orgApiEndpoint } = useEndpointsContext();
  const queryClient = useQueryClient();
  return useMutation<IHedgeRule, undefined, HedgeRuleCreationMutationArgs>({
    mutationFn: async payload =>
      request<MinimalSubscriptionResponse<IHedgeRule>>('POST', `${orgApiEndpoint}/hedge-rules`, payload).then(res => {
        return res.data[0];
      }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [HEDGE_RULE_ID] });
    },
  });
}

type HedgeRuleModificationMutationArgs = {
  id: string;
  payload: HedgeRulePatchPayload;
};
export function useHedgeRuleModificationMutation() {
  const { orgApiEndpoint } = useEndpointsContext();
  const queryClient = useQueryClient();
  return useMutation<IHedgeRule, undefined, HedgeRuleModificationMutationArgs>({
    mutationFn: async ({ id, payload }) =>
      request<MinimalSubscriptionResponse<IHedgeRule>>('PATCH', `${orgApiEndpoint}/hedge-rules/${id}`, payload).then(
        res => {
          return res.data[0];
        }
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [HEDGE_RULE_ID] });
    },
  });
}

export function useHedgeRuleDeletionMutation() {
  const { orgApiEndpoint } = useEndpointsContext();
  const queryClient = useQueryClient();
  return useMutation<void, undefined, string>({
    mutationFn: async id => {
      await request<MinimalSubscriptionResponse<IHedgeRule>>('DELETE', `${orgApiEndpoint}/hedge-rules/${id}`);
    },
    onSuccess: (_data, id) => {
      queryClient.setQueryData(generateHedgeRuleQueryID(id), null);
      queryClient.setQueryData<MinimalSubscriptionResponse<IHedgeRule>>([HEDGE_RULE_ID], old => {
        if (!old) {
          return old;
        }
        return {
          ...old,
          data: old.data.filter(rule => rule.HedgeRuleID !== id),
        };
      });
    },
    onSettled: (_data, _err, id) => {
      queryClient.invalidateQueries({ queryKey: generateHedgeRuleQueryID(id) });
      queryClient.invalidateQueries({ queryKey: [HEDGE_RULE_ID] });
    },
  });
}

type HedgeRuleCommandMutationArgs = SingleHedgeCommandPayload | GlobalHedgeCommandPayload;
export function useHedgeRuleCommandMutation() {
  const { orgApiEndpoint } = useEndpointsContext();
  const queryClient = useQueryClient();
  return useMutation<HedgeCommandResponse, undefined, HedgeRuleCommandMutationArgs>({
    mutationFn: async payload => {
      const HedgeCommandID = uuid();
      return request<MinimalSubscriptionResponse<HedgeCommandResponse>>('POST', `${orgApiEndpoint}/hedge-command`, {
        ...payload,
        HedgeCommandID,
      }).then(res => res.data[0]);
    },
    onSettled: (_data, _err, origPayload) => {
      if ('HedgeRuleID' in origPayload) {
        queryClient.invalidateQueries({ queryKey: generateHedgeRuleQueryID(origPayload.HedgeRuleID) });
      }
      // invalidate all the hedge rules (tables showing the updated rule should refetch)
      queryClient.invalidateQueries({ queryKey: [HEDGE_RULE_ID] });
    },
    mutationKey: ['HedgeRuleCommand'] as const,
  });
}
